import React, { useState } from 'react';
import cx from 'classnames';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import stc from 'string-to-color';
import { getFullName, getInitials, toUserTimezone } from 'utils';
import Email from './Email';
import SentIcon from '@material-ui/icons/Done';
import DeliveredIcon from '@material-ui/icons/DoneAll';
import SmsFailedIcon from '@material-ui/icons/Info';
import { Link } from 'react-router-dom';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import MoreVertIcon from '@material-ui/icons/MoreHoriz';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import { useMutation } from '@apollo/react-hooks';
import {JOIN_CONVO, UPDATE_USER_CONVO_STATUS} from 'graphql/conversations';
import NotifyModal from './NotifyModal';
import MessageAttachment from 'modules/student/chat/components/MessageAttachment';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    padding: theme.spacing(2),
  },
  left: {
    marginRight: 'auto',
  },
  right: {
    marginLeft: 'auto',
  },
  bubble: {
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1.5),
  },
  bubbleOther: {
    backgroundColor: '#e8e8e8',
    color: '#222',
  },
  bubbleincoming: {
    color: '#224E41',
    backgroundColor: '#E0F2F1',
  },
  textRight: {
    textAlign: 'right',
  },
  avatarSmall: {
    width: 24,
    height: 24,
    fontSize: 10,
  },
  users: {
    marginRight: 16,
    display: 'flex',
    lineHeight: '14px',
  },
  sender: {
    marginRight: 4,
    marginLeft: 4,
    fontWeight: 500,
  },
  icon: {
    fontSize: 14,
  },
  link: {
    'a&:visited': {
      color: '#12263f',
    },
  },
}));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      'backgroundColor': theme.palette.primary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Message = ({ message, user, isFirst, isLast }) => {
  const classes = useStyles();
  const [showNotify, setShowNotify] = useState(false);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'messageMenu',
  });
  const [joinConvo] = useMutation(JOIN_CONVO);

  const {
    convo_id,
    type,
    text,
    incoming,
    created_at,
    user: creator,
    receipts,
    attachments
  } = message;

  if (type === 'email') {
    return (
      <Email message={message} user={user} isFirst={isFirst} isLast={isLast} />
    );
  }

  const markUnread = () => {
    joinConvo({
      variables: {
        convoId: convo_id,
        userId: user.id,
        timestamp: moment(created_at)
          .subtract(1, 's')
          .toJSON(),
      },
    });
    popupState.close();
  };

  const hideNotify = () => {
    setShowNotify(false);
    popupState.close();
  }

  return (
    <div
      id={`mid-${message.id}`}
      className={cx(classes.root, {
        [classes.left]: incoming,
        [classes.right]: !incoming,
      })}
    >
      {incoming && creator.user_type === 'student' ? (
        <Link to={`/students/${creator.user_id}/timeline`}>
          <Avatar
            style={{
              marginRight: 8,
              alignSelf: 'center',
              backgroundColor: stc(creator.user_email),
            }}
            src={creator.avatar}
          >
            {getInitials(creator.user_first_name, creator.user_last_name)}
          </Avatar>
        </Link>
      ) : (
        <Avatar
          style={{
            marginRight: 8,
            alignSelf: 'center',
            backgroundColor: stc(creator.user_email),
          }}
          src={creator.avatar}
        >
          {getInitials(creator.user_first_name, creator.user_last_name)}
        </Avatar>
      )}
      <Box flex={1}>
        <Box display="flex" alignItems="center">
          <Typography
            component="p"
            variant="body2"
            className={cx(classes.sender, !incoming && classes.textRight)}
          >
            {creator.user_type === 'student' ? (
              <Link
                className={classes.link}
                to={`/students/${creator.user_id}/timeline`}
              >
                {getFullName(creator)}
              </Link>
            ) : (
              getFullName(creator)
            )}
          </Typography>
          <Typography variant="caption">
            {toUserTimezone(created_at, user.timezone).format('h:mm A')}
          </Typography>
          {incoming && (
            <Box style={{ marginLeft: 'auto' }}>
              <IconButton size="small" {...bindTrigger(popupState)}>
                <MoreVertIcon />
              </IconButton>
              <Menu {...bindMenu(popupState)}>
                <StyledMenuItem onClick={markUnread}>
                  <ListItemText primary="Mark Unread" />
                </StyledMenuItem>
                {user.type === 'admin' && (
                  <StyledMenuItem onClick={() => setShowNotify(true)}>
                    <ListItemText primary="Notify Coaches" />
                  </StyledMenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Box>
        <>
          <div
            className={cx(classes.bubble, {
              [classes.bubbleincoming]: incoming,
              [classes.bubbleOther]: !incoming,
            })}
          >
            <Box>
              <Typography color="inherit" variant="body1">
                {text}
              </Typography>
              { attachments && attachments.length ? (
                <Box marginTop={1}>
                  { attachments.map(i => <MessageAttachment key={i.name} url={i.url} /> )}
                </Box>
              ) : null}
              <Typography variant="caption" className={classes.users}>
                {receipts.map(({ status }) => (
                  <Box marginLeft="auto">
                    <Box>
                      {!incoming && status === 'sent' && (
                        <SentIcon className={classes.icon} />
                      )}
                      {!incoming && status === 'delivered' && (
                        <DeliveredIcon
                          color="primary"
                          className={classes.icon}
                        />
                      )}
                      {!incoming &&
                        (status === 'undelivered' || status === 'failed') && (
                          <Tooltip title="SMS sending Failed">
                            <SmsFailedIcon
                              color="secondary"
                              className={classes.icon}
                            />
                          </Tooltip>
                        )}
                    </Box>
                  </Box>
                ))}
              </Typography>
            </Box>
          </div>
        </>
      </Box>
      {showNotify && (
        <NotifyModal user={user} message={message} onClose={hideNotify} />
      )}
    </div>
  );
};

export default Message;
